/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Input, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import * as _ from 'lodash';
import template from './third-party-audience-list-page.component.html';

import {APP_SHARED} from '../common/app-shared';
import {SignalAdminResourceListComponent} from '../common/controller/signal-admin-resource-list.component';
import {
  SignalAdminListColumn,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {ThirdPartyAudienceModalService} from './third-party-audience-modal.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {ThirdPartyAudienceApiService, ThirdPartyAudienceModel} from './third-party-audience-api.service';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, UIRouterModule, SignalAdminResourceListComponent],
  selector: 'adn-third-party-audience-list-page',
  template
})
export class ThirdPartyAudienceListPageComponent implements OnInit {
  readonly itemStateName = 'app.adv.third-party-audiences.third-party-audience';
  readonly providers = ['NORDIC_DATA_RESOURCES', 'LC_MEDIA'];

  providerFilter = '';

  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'provider'},
    {param: 'externalReference'},
    {param: '', label: 'teams', sortable: false}
  ];
  readonly list = inject(SignalAdminResourceListStateFactory).create<ThirdPartyAudienceModel>(
    inject(ThirdPartyAudienceApiService),
    {listId: 'fullThirdPartyAudienceList'},
    inject(DestroyRef)
  );

  private readonly hasManageSystem: boolean;

  constructor(
    private readonly LocalUserPermissions: LocalUserPermissions,
    private readonly thirdPartyAudienceModalService: ThirdPartyAudienceModalService
  ) {
    this.hasManageSystem = LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM');
  }

  @Input()
  set readOnly(value: boolean) {
    this.list.readOnly.set(value);
  }

  ngOnInit(): void {
    this.list.load();
  }

  applyProviderFilter(): void {
    const params = {...this.list.additionalParams()};
    if (this.providerFilter) {
      params.filterBy = 'provider';
      params.filterByValue = this.providerFilter;
    } else {
      delete params.filterBy;
      delete params.filterByValue;
    }
    this.list.setAdditionalParams(params);
  }

  isAudienceReadOnly(audience: any): boolean {
    if (this.hasManageSystem) {
      return false;
    }
    if (_.isEmpty(audience.teams)) {
      return true;
    }
    return _.some(audience.teams, (team: any) => {
      return !this.LocalUserPermissions.has('MANAGE_AUDIENCES', team.id);
    });
  }

  hasData(audience: any): boolean {
    if (audience.provider && audience.metaData) {
      return _.get(audience.metaData, audience.provider) || null;
    }
    return false;
  }

  openAudienceUploadModal(uploadType: 'data' | 'definitions'): void {
    this.thirdPartyAudienceModalService.openUploadModal(uploadType, () => {
      this.list.load();
    });
  }
}
